import QRCode, { QRCodeCanvas } from 'qrcode.react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';

import { Config } from '../../common/config';
import { formatGiftSerialDisplay } from '../../utils/formatGiftSerial';
import { prepareGiftImage } from '../../utils/resizeGiftImage';
import AlertModal from '../../components/alertModal';
import LeftMenu from '../../components/leftMenu';
import {
  formatStrNumber,
  getFormattedDateTime,
  delComString,
  formatNumNumber,
  toSeoulDateString,
  getErrorMessage,
  isSuccessResponse,
} from '../../components/utils';
import translations from '../../data/translation';
import LocaleDateInput from '../../components/LocaleDateInput';
import useAxiosFetch from '../../hooks/useAxiosFetch';
import useStoreNo from '../../hooks/useStoreNo';
import GiftCouponPicker, { ISSUER_LABEL } from '../../components/GiftCouponPicker';
import qrStyles from '../../css/admin/QR.module.css';
import {
  getGiftProductQrStickerStrings,
  giftQrStickerToDataURL,
} from '../../utils/giftQrStickerCanvas';

export default function GiftManage() {
  const navigate = useNavigate();

  const location = useLocation();
  const storeNo = useStoreNo();
  const language = useSelector(state => state.language.language); // redux에서 언어 상태 가져오기
  const t = translations[language]; // 현재 언어에 따른 번역 객체 가져오기
  const receivedData = location.state || {};
  const giftNo = receivedData.giftNo;

  const [modalOpen, setModalOpen] = useState(false); //모달 상태 관리 : 기본값 - 닫힘
  const [modalInfo, setModalInfo] = useState({
    isCancel: false,
    isConfirm: true,
    isMessage: true,
    isStopFirst: false,
    headerMsg: '',
    actionFlag: '',
    mainMsg: '',
    action: '',
    isPercent: false,
  });
  const [giftData, setGiftData] = useState({});
  const [categoryData, setCategoryData] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imageFileName, setImageFileName] = useState(null);
  const [giftDesc, setGiftDesc] = useState('');
  const [openAIGiftDescData, setOpenAIGiftDescData] = useState([]);
  const [openAISelectedData, setOpenAISelectedcData] = useState('');
  const [saleEndDttm, setSaleEndDttm] = useState('');
  const [saleStartDttmPre, setSaleStartDttmPre] = useState('');
  const [saleEndDttmPre, setSaleEndDttmPre] = useState('');
  // 불러온 기프트의 원래 판매종료일(YYYY-MM-DD, 무기한이면 '') — 미래 시작 수정 확인창 안내용
  const [origSaleEndDttm, setOrigSaleEndDttm] = useState('');
  // 유효기간은 약관에 따라 3개월로 고정
  const [validityMonth, setValidityMonth] = useState('3');
  const [validityDay, setValidityDay] = useState('');
  const [validityMonthPre, setValidityMonthPre] = useState('3');
  const [validityDayPre, setValidityDayPre] = useState('');
  const [countIssue, setCountIssue] = useState('');
  const [countIssuePre, setCountIssuePre] = useState('');
  const [isSalePeriodChecked, setIsSalePeriodChecked] = useState(false);
  const [isValidityChecked, setIsValidityChecked] = useState(true);
  const [isCountIssueChecked, setIsCountIssueChecked] = useState(false);
  const [regDttm, setRegDttm] = useState('');
  const [updDttm, setUpdDttm] = useState('');
  const [stopDesc, setStopDesc] = useState('');
  const [modDone, setModDone] = useState(false);
  // [2026-10-19] upstream 66270b0(바우처 실증 종료) 반영 — 상점 바우처 잔액 표시는 복구 안 함
  // const [storeVoucherInfo, setStoreVoucherInfo] = useState(null);
  // 프로모션 선택 드롭다운용 VOUCHER 목록 — 선택값은 giftData.discountPromotionNo
  const [availableVouchers, setAvailableVouchers] = useState([]);
  const [vouchersLoaded, setVouchersLoaded] = useState(false);
  // [2026-10-27] 쿠폰 적용 허용(Y)일 때 이 기프트에 쓸 쿠폰 선택 — 후보는 gift/available-coupons/:storeNo
  const [couponCandidates, setCouponCandidates] = useState([]);
  const [couponsLoaded, setCouponsLoaded] = useState(false);
  const [selectedCouponNos, setSelectedCouponNos] = useState([]);
  // 이미 골라져 있지만 종료·중지돼 후보에 없는 쿠폰 (회색 "(종료)" 표시)
  const [endedCoupons, setEndedCoupons] = useState([]);
  // 선택 기록이 없는 기존 허용 기프트 — 후보를 받으면 전체 체크로 연다
  const [pendingSelectAllCoupons, setPendingSelectAllCoupons] = useState(false);
  const [couponSelectError, setCouponSelectError] = useState('');

  // Inline validation error messages
  const [priceError, setPriceError] = useState('');
  const [basePriceError, setBasePriceError] = useState('');
  const [discountPriceError, setDiscountPriceError] = useState('');
  const [validityError, setValidityError] = useState('');
  const [countIssueError, setCountIssueError] = useState('');
  const [imageError, setImageError] = useState('');
  const basePriceNumber = Number(delComString(String(giftData.basePrice ?? '')) || '0');
  // [2026-10-21] 불러온 기프트에 연결된 프로모션이 선택 가능 목록에 없음(기간 종료 등) — 목록을 받은 뒤에만 판단
  const isStaleVoucher =
    vouchersLoaded &&
    !!giftData.discountPromotionNo &&
    !availableVouchers.some(v => String(v.promotionNo) === String(giftData.discountPromotionNo));
  const isPriceLocked = giftData.state === 'SS004' || giftData.state === 'SS005';
  // [2026-10-30] 할인가격은 바우처 프로모션을 골랐을 때만 입력 · 저장 — 프로모션 없는 할인은 저장하면 해제된다
  //  (수정 불가 상태는 보기만 하므로 기존 값 그대로 보여준다)
  const canDiscount = !!giftData.discountPromotionNo && !isStaleVoucher;
  const loadedDiscountNumber = Number(delComString(String(giftData.discountPrice ?? '')) || '0');
  const discountPriceNumber = isPriceLocked || canDiscount ? loadedDiscountNumber : 0;
  const isDiscountDropped = !isPriceLocked && !canDiscount && loadedDiscountNumber > 0;
  const effectivePrice = discountPriceNumber > 0 ? basePriceNumber - discountPriceNumber : basePriceNumber;
  const giftPriceNumber = effectivePrice || Number(delComString(String(giftData.price ?? '')) || '0');
  const isZeroPriceGift = giftPriceNumber === 0;
  const isStampDutyTarget = giftPriceNumber >= 50000;

  const getCategoryLabel = codeOrName => {
    const matched = categoryData.find(
      category => category.detailCode === codeOrName
    );
    const name = matched ? matched.detailCodeName : codeOrName;
    return t.selectCategory[name] || name || '';
  };

  const { fetchData } = useAxiosFetch();

  const qrDownloadRef = useRef(null);
  const effectiveStoreNo = giftData.storeNo ?? storeNo;
  const effectiveGiftNo = giftData.giftNo ?? giftNo;
  const giftProductUrl =
    Config.SHOPUSER_SERVER &&
    effectiveStoreNo != null &&
    effectiveStoreNo !== '' &&
    effectiveGiftNo != null &&
    effectiveGiftNo !== ''
      ? `${Config.SHOPUSER_SERVER}gift/${effectiveStoreNo}/${effectiveGiftNo}`
      : '';

  // [APP-FORK] QR 은 항상 uuid 스킴(백엔드 조립: {SHOPUSER_APP_URL}/app/link/gift/{uuid}).
  //   ★도메인/UA 로 분기하지 않는다 — QR 은 누가 스캔할지 모른다(외부 카메라/앱 스캐너 모두).
  //     "지금 앱에서 보고 있나"로 갈라봐야 스캔하는 쪽과 무관하고, 앱 전용 진입 목적도 깨진다.
  //   ★웹URL 폴백을 두지 않는다 — 폴백은 숫자 PK(/gift/{storeNo}/{giftNo})를 노출하고,
  //     무엇보다 조용히 성립해 잘못된 QR 이 배포돼도 아무도 모른다. 값이 없으면 QR 을 안 그린다.
  // 이동링크(href)는 giftProductUrl(URL) 그대로 사용.
  const qrValue = giftData.qrSchemeValue || '';

  const getQrCanvas = () => {
    return qrDownloadRef.current?.querySelector('canvas');
  };

  const handleDownloadGiftProductQr = async () => {
    const canvas = getQrCanvas();
    if (!canvas) return;
    try {
      const sticker = getGiftProductQrStickerStrings(
        language,
        giftData.giftName
      );
      const dataUrl = await giftQrStickerToDataURL(canvas, {
        ...sticker,
        scale: 2,
      });
      const pngUrl = dataUrl.replace('image/png', 'image/octet-stream');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `naegift-gift-qr-${effectiveGiftNo || 'gift'}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (err) {
      console.error('Gift product QR download failed:', err);
    }
  };

  const handlePrintGiftProductQr = () => {
    const canvas = getQrCanvas();
    if (!canvas) return;
    try {
      const pngUrl = canvas.toDataURL('image/png');
      const { storeName, giftLabel, scanText, footerParagraphs } =
        getGiftProductQrStickerStrings(language, giftData.giftName);
      const footerText = footerParagraphs.join('<br />');

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <title>${storeName} ${giftLabel} QR</title>
          <style>
            * { box-sizing: border-box; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Malgun Gothic', 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 24px;
              min-height: 100vh;
              display: flex;
              justify-content: center;
              align-items: center;
              background: #f5f5f5;
            }
            .storeQrCard {
              box-sizing: border-box;
              width: 200px;
              padding: 0 0 14px 0;
              border-radius: 10px;
              overflow: visible;
              background: #ffffff;
              display: flex;
              flex-direction: column;
              align-items: stretch;
              gap: 0;
              border: 1px solid rgba(15, 23, 42, 0.08);
              box-shadow:
                0 0 0 1px rgba(255, 255, 255, 0.75) inset,
                0 1px 0 rgba(255, 255, 255, 0.9) inset,
                0 -1px 0 rgba(15, 23, 42, 0.04) inset,
                0 2px 4px rgba(15, 23, 42, 0.04),
                0 8px 16px rgba(15, 23, 42, 0.08),
                0 16px 32px rgba(15, 23, 42, 0.1);
              transform: translateY(-2px);
              position: relative;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .storeQrHead {
              background: #ffffff;
              padding: 18px 14px 12px 14px;
              border-radius: 10px 10px 0 0;
              box-shadow: 0 1px 0 rgba(15, 23, 42, 0.06);
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .storeQrTitle {
              font-size: 16px;
              font-weight: 600;
              text-align: center;
              color: #000000;
            }
            .storeQrScanText {
              font-size: 13px;
              font-weight: 500;
              color: #000000;
            }
            .storeQrBody {
              box-sizing: border-box;
              margin: 10px 14px 4px 14px;
              padding: 10px;
              border-radius: 8px;
              background: linear-gradient(180deg, #fafafa 0%, #ffffff 45%);
              display: flex;
              align-items: center;
              justify-content: center;
              border: 1px solid rgba(15, 23, 42, 0.08);
              box-shadow:
                inset 0 1px 1px rgba(255, 255, 255, 0.9),
                inset 0 -2px 6px rgba(15, 23, 42, 0.05),
                0 1px 2px rgba(15, 23, 42, 0.04);
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .storeQrBody img {
              display: block;
              width: 120px;
              height: 120px;
              object-fit: contain;
            }
            .storeQrFooter {
              box-sizing: border-box;
              font-size: 12px;
              text-align: center;
              opacity: 0.95;
              margin: 0 14px;
              padding: 6px 8px;
              border-radius: 6px;
              background: #f8fafc;
              color: #000000;
              line-height: 1.4;
              border: 1px solid rgba(15, 23, 42, 0.06);
              box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            @media print {
              body { background: #fff; padding: 16px; }
            }
          </style>
        </head>
        <body>
          <div class="storeQrCard">
            <div class="storeQrHead">
              <div class="storeQrTitle">
                ${storeName} ${giftLabel}
                <br />
                <span class="storeQrScanText">${scanText}</span>
              </div>
            </div>
            <div class="storeQrBody"><img src="${pngUrl}" alt="QR" /></div>
            <div class="storeQrFooter">${footerText}</div>
          </div>
        </body>
        </html>
      `;

      const printWin = window.open('', '_blank');
      if (!printWin) {
        alert(
          language === 'en'
            ? 'Please allow pop-ups to print.'
            : '프린트를 위해 팝업을 허용해 주세요.'
        );
        return;
      }
      printWin.document.write(printContent);
      printWin.document.close();
      printWin.onload = () => {
        printWin.focus();
        printWin.print();
        printWin.onafterprint = () => printWin.close();
      };
    } catch (err) {
      console.error('Gift product QR print failed:', err);
    }
  };

  const openModal = () => {
    if (modalInfo.actionFlag === 'stop') {
      const msg =
        giftData.state === 'SS001'
          ? t.confirmSaleStartReplace
          : giftData.state === 'SS003'
            ? t.confirmResume
            : t.confirmPause;
      setModalInfo({
        ...modalInfo,
        actionFlag: 'stop',
        isStopFirst: true,
        isCancel: true,
        mainMsg: msg,
      });
    } else if (modalInfo.actionFlag === 'mod') {
      if (giftData.state === 'SS004' || giftData.state === 'SS005') {
        setModalInfo({
          ...modalInfo,
          actionFlag: '',
          isCancel: false,
          mainMsg: t.giftSaleEndNoModify,
        });
      } else {
        // 판매중·판매중지 기프트를 미래 시작일로 수정하면 기존 기프트는 새 시작 전날까지 계속 판매된다
        const newStart = toSeoulDateString(giftData.saleStartDttm);
        const todayStr = toSeoulDateString(new Date());
        let mainMsg = t.confirmModify;
        if (
          (giftData.state === 'SS002' || giftData.state === 'SS003') &&
          newStart > todayStr
        ) {
          const prevDay = toSeoulDateString(
            new Date(
              new Date(`${newStart}T00:00:00+09:00`).getTime() - 24 * 60 * 60 * 1000
            )
          );
          const oldUntil =
            origSaleEndDttm && origSaleEndDttm < prevDay
              ? origSaleEndDttm
              : prevDay;
          mainMsg = t.confirmModifyFuture
            .replace('{oldUntil}', oldUntil)
            .replace('{newStart}', newStart);
        } else if (newStart === todayStr) {
          // 시작일이 오늘이면 저장 즉시 판매가 시작된다는 걸 알리고 한 번 더 묻는다.
          // 판매예정 버전은 대체하려던 이전 버전도 함께 판매취소, 판매중·판매중지는 기존 기프트가 판매취소된다.
          const pendingStart = giftData.pendingSaleStartDttm
            ? toSeoulDateString(giftData.pendingSaleStartDttm)
            : '';
          if (giftData.state === 'SS001') {
            mainMsg = t.confirmModifyTodayReplace;
          } else if (pendingStart) {
            // 판매예정 버전이 있으면 고친 내용은 예약 시작 전날까지만 팔리고, 예약은 그대로 시작된다
            const pendingPrevDay = toSeoulDateString(
              new Date(
                new Date(`${pendingStart}T00:00:00+09:00`).getTime() - 24 * 60 * 60 * 1000
              )
            );
            const newEnd = saleEndDttm ? toSeoulDateString(saleEndDttm) : '';
            const until =
              newEnd && newEnd < pendingPrevDay ? newEnd : pendingPrevDay;
            mainMsg = t.confirmModifyTodayWithPending
              .replace('{until}', until)
              .replace('{pendingStart}', pendingStart);
          } else {
            mainMsg = t.confirmModifyToday;
          }
        }
        setModalInfo({
          ...modalInfo,
          actionFlag: 'mod',
          isCancel: true,
          mainMsg,
        });
      }
    } else if (modalInfo.actionFlag === 'del') {
      setModalInfo({
        ...modalInfo,
        actionFlag: 'del',
        isCancel: true,
        mainMsg: t.confirmCancelSale,
      });
    } else if (modalInfo.actionFlag === 'api') {
      setModalInfo({
        ...modalInfo,
        mainMsg: modalInfo.mainMsg || t.requestError,
      });
    }

    setModalOpen(true);
  };

  const closeModal = () => {
    setModalInfo({
      ...modalInfo,
      actionFlag: '',
      isCancel: false,
      isConfirm: true,
      img: null,
    });
    setModalOpen(false);
  };

  const doModal = () => {
    setModalOpen(false);
    if (modalInfo.actionFlag === 'modSuccess') {
      modalInfo.actionFlag = '';
      navigate(storeNo ? `/${storeNo}/gift-list` : '/gift-list');
    } else if (modalInfo.actionFlag === 'mod') {
      modalInfo.actionFlag = '';
      modGift();
    } else if (modalInfo.actionFlag === 'stop') {
      modalInfo.actionFlag = '';
      toggleSale();
    } else if (modalInfo.actionFlag === 'del') {
      modalInfo.actionFlag = '';
      delGift();
    } else if (modalInfo.actionFlag === 'goMain') {
      navigate('/go-main');
    } else if (modalInfo.actionFlag === 'openai') {
      modalInfo.actionFlag = '';

      setGiftDesc(openAISelectedData ? openAISelectedData : giftData.giftDesc);
    }
  };

  const categoryChange = e => {
    setGiftData({ ...giftData, categoryCode: e.target.value });
  };

  const giftDescChange = e => {
    const selected = e.target.value;
    setOpenAISelectedcData(selected);
    setGiftDesc(selected);
  };

  const salePeriodCheckboxChange = e => {
    setIsSalePeriodChecked(!isSalePeriodChecked);

    if (e.target.checked) {
      setSaleEndDttm('');
    } else {
      setSaleEndDttm(saleEndDttmPre);
    }
  };

  // 유효기간은 3개월로 고정되므로 체크박스 변경은 의미 없음
  const validyCheckboxChange = _e => {
    setIsValidityChecked(true);
    setValidityDay('');
    setValidityMonth('3');
    setValidityMonthPre('3');
  };

  const countIssueCheckboxChange = e => {
    setIsCountIssueChecked(!isCountIssueChecked);

    if (e.target.checked) {
      setCountIssue('');
    } else {
      setCountIssue(countIssuePre);
    }
  };

  // JWT refresh handled centrally in useAxiosFetch

  const getData = useCallback(
    async _unused => {
      try {
        const ret = await fetchData({
          url: `${Config.API_ADMIN_CODE_DETAIL}?pageSize=20&sortOrd=asc&groupCode=NG02`,
          method: 'get',
        });
        if (ret?.data?.data) {
          setCategoryData(ret.data.data.list);
        }
        const ret1 = await fetchData({
          url: process.env.REACT_APP_API_SERVER + 'gift/' + giftNo,
          method: 'get',
        });
        if (!isSuccessResponse(ret1)) {
          setModalInfo(prev => ({
            ...prev,
            actionFlag: 'api',
            mainMsg: getErrorMessage(ret1),
          }));
          setModalOpen(true);
          return;
        }
        if (ret1?.data?.data) {
          const loaded = ret1.data.data;
          const todayStr = toSeoulDateString(new Date());
          const loadedSaleStart = toSeoulDateString(loaded.saleStartDttm);
          const saleStartDttm =
            loadedSaleStart && loadedSaleStart < todayStr
              ? todayStr
              : loadedSaleStart || todayStr;
          // 바우처 실증기간 종료 — 수정 가능한 기프트는 바우처 미적용(N)으로 저장되므로 화면도 그 기준으로 맞춘다.
          // 할인가격은 바우처와 무관한 상점 자체 할인이라 상태와 관계없이 현재 값을 그대로 보여준다.
          const readOnlyGift = loaded.state === 'SS004' || loaded.state === 'SS005';
          const apiDiscountPrice = loaded.discountPrice > 0 ? loaded.discountPrice : 0;
          const discountAmount = (apiDiscountPrice > 0 && loaded.basePrice > 0)
            ? loaded.basePrice - apiDiscountPrice
            : 0;
          setGiftData({
            ...loaded,
            saleStartDttm,
            discountPrice: discountAmount || '',
            // voucherUseYn: readOnlyGift ? loaded.voucherUseYn : 'N',
            // 바우처 토글 UI 없음 — 저장 시 discountPromotionNo 유무로 voucherUseYn 을 다시 정한다
            voucherUseYn: readOnlyGift ? loaded.voucherUseYn : (loaded.discountPromotionNo ? 'Y' : 'N'),
            discountPromotionNo: loaded.discountPromotionNo ?? '',
          });
          const loadedCouponNos = (loaded.couponPromotionNos || []).map(Number);
          setSelectedCouponNos(loadedCouponNos);
          setEndedCoupons((loaded.couponPromotions || []).filter(c => c.ended));
          setPendingSelectAllCoupons((loaded.couponEligibleYn || 'Y') === 'Y' && loadedCouponNos.length === 0);
          setCouponSelectError('');
          if (ret1.data.data.thumbnailFileUrl !== Config.API_SERVER) {
            setImageFile(ret1.data.data.thumbnailFileUrl);
          }
          const match1 = getMatch(ret1.data.data.validity);
          if (match1) {
            if (match1.unitPart === 'M') {
              setValidityMonth(match1.numberPart);
              setValidityMonthPre(match1.numberPart);
            } else {
              setValidityDay(match1.numberPart);
              setValidityDayPre(match1.numberPart);
            }
          }
          setGiftDesc(ret1.data.data.giftDesc);
          setCountIssue(formatNumNumber(ret1.data.data.countIssued));
          setCountIssuePre(formatNumNumber(ret1.data.data.countIssued));
          setSaleStartDttmPre(toSeoulDateString(ret1.data.data.saleStartDttm));
          setOrigSaleEndDttm(toSeoulDateString(ret1.data.data.saleEndDttm));
          if (ret1.data.data.saleEndDttm) {
            setSaleEndDttm(toSeoulDateString(ret1.data.data.saleEndDttm));
            setSaleEndDttmPre(toSeoulDateString(ret1.data.data.saleEndDttm));
          }
          setRegDttm(getFormattedDateTime(ret1.data.data.regDttm));
          setUpdDttm(getFormattedDateTime(ret1.data.data.updDttm));

          const adRet = await fetchData({
            url: `${Config.API_SERVER}gift/${giftNo}/ad-copies`,
            method: 'get',
          });
          if (isSuccessResponse(adRet)) {
            const copies = (adRet?.data?.data?.copies || []).slice(0, 5);
            if (copies.length > 0) {
              setOpenAIGiftDescData(copies);
              setOpenAISelectedcData(copies[0]);
              if (!String(ret1.data.data.giftDesc || '').trim()) {
                setGiftDesc(copies[0]);
              }
            }
          }
        }
        if (ret1.data.data.countIssued == null) {
          setIsCountIssueChecked(true);
        }
        if (ret1.data.data.saleEndDttm === null) {
          setIsSalePeriodChecked(true);
        }
      } catch (error) {}
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [giftNo]
  );

  const getMatch = mixedData => {
    const match = mixedData.match(/^(\d+)(\D+)/);

    if (match) {
      const numberPart = match[1];
      const unitPart = match[2];

      const sepData = { numberPart, unitPart };

      return sepData;
    } else {
      return null;
    }
  };

  const changeFile = async e => {
    const input = e.target;
    const picked = input.files[0];
    if (picked) {
      // 큰 사진은 긴 변 1,500px로 자동 축소 — 줄여도 3MB를 넘을 때(큰 GIF 등)만 안내
      const file = await prepareGiftImage(picked);
      if (!file) {
        input.value = null; // 파일 선택 취소
        setImageFile(null);
        setImageFileName(null);
        setImageError(t.imageSizeError);
      } else {
        setImageError('');
        setImageFile(URL.createObjectURL(file)); // 미리보기
        setImageFileName(file);
      }
    } else {
      setImageFile(null);
      setImageFileName(null);
      setImageError('');
    }
  };

  const changeDesc = async () => {
    try {
      setModalInfo({
        ...modalInfo,
        actionFlag: 'adCopyLoading',
        mainMsg: '',
        isCancel: false,
        isConfirm: false,
        img: null,
      });
      openModal();

      const ret = await fetchData({
        url: `${Config.API_SERVER}gift/${giftNo}/ad-copies/generate`,
        method: 'post',
      });
      if (!isSuccessResponse(ret)) {
        setModalInfo({
          ...modalInfo,
          actionFlag: 'api',
          mainMsg: getErrorMessage(ret),
          isCancel: false,
          isConfirm: true,
          img: null,
        });
        setModalOpen(true);
        return;
      }

      const copies = (ret?.data?.data?.copies || []).slice(0, 5);
      setOpenAIGiftDescData(copies);
      setOpenAISelectedcData(copies[0] || giftDesc || giftData.giftDesc || '');
      setModalInfo({
        ...modalInfo,
        actionFlag: 'openai',
        isCancel: true,
        isConfirm: true,
        img: null,
      });
      setModalOpen(true);
    } catch (error) {
      setModalInfo({
        ...modalInfo,
        actionFlag: 'api',
        mainMsg: getErrorMessage(error?.response),
        isCancel: false,
        isConfirm: true,
        img: null,
      });
      setModalOpen(true);
      console.error('Error fetching data:', error);
    }
  };

  // 검증 통과 시 true, 실패 시 에러 메시지 문자열을 반환한다.
  // (과거에는 여기서 직접 setModalInfo로 에러 메시지를 예약했는데, modGift()의
  // 실패 분기가 그 직후 stale한 modalInfo로 다시 덮어써서 메시지가 사라지는
  // 버그가 있었다. 호출부에서 반환값을 그대로 최신 상태에 적용하도록 변경.)
  const checkModGift = () => {
    // [2026-10-27] 쿠폰 허용인데 고를 수 있는 쿠폰이 있으면 1개 이상 골라야 한다 (서버도 400으로 막음)
    if (
      (giftData.couponEligibleYn || 'Y') === 'Y' &&
      couponsLoaded &&
      couponCandidates.length > 0 &&
      selectedCouponNos.length === 0
    ) {
      setCouponSelectError('적용할 쿠폰을 1개 이상 선택하세요.');
      return '적용할 쿠폰을 1개 이상 선택하세요.';
    }
    if (!giftData.giftName) {
      return t.giftNameRequired;
    }
    if (giftData.giftName && giftData.giftName.length > 50) {
      return t.giftNameMaxLength;
    }
    const basePriceNum = Number(delComString(String(giftData.basePrice ?? '')) || '0');
    if (giftData.basePrice === '' || giftData.basePrice == null || Number.isNaN(basePriceNum)) {
      return '정상가격을 입력해 주세요.';
    }
    if (basePriceNum > 1000000) {
      return t.giftPriceMax;
    }
    if (basePriceNum !== 0 && basePriceNum % 100 !== 0) {
      return t.giftPriceUnit;
    }
    // 판매종료일은 날짜를 고르거나 무기한을 체크해야 한다 (비워두면 무기한으로 저장되던 것 방지)
    if (!isSalePeriodChecked && !saleEndDttm) {
      return t.saleEndRequired;
    }
    // 총발행수량은 직접 입력하거나 무제한을 체크해야 한다 (비워두면 무제한으로 저장되던 것 방지)
    const countNum = Number(delComString(String(countIssue ?? '')) || '0');
    if (!isCountIssueChecked && !(countNum > 0)) {
      return t.countIssueRequired;
    }
    const discountPriceNum = discountPriceNumber;
    if (discountPriceNum % 100 !== 0) {
      return t.giftPriceUnit;
    }
    if (discountPriceNum > 0 && discountPriceNum >= basePriceNum) {
      return '할인가격은 정상가격보다 작아야 합니다.';
    }
    // [2026-10-21] 할인가격이 선택한 프로모션 잔여 예산을 넘으면 저장 불가 (서버도 400으로 막음)
    if (giftData.discountPromotionNo && discountPriceNum > 0) {
      const v = availableVouchers.find(x => String(x.promotionNo) === String(giftData.discountPromotionNo));
      const remaining = v ? (v.totalAmount ?? 0) - (v.usedAmount ?? 0) : null;
      if (remaining != null && discountPriceNum > remaining) {
        return `할인가격(${formatNumNumber(discountPriceNum)}원)이 선택한 프로모션의 잔여 예산(${formatNumNumber(remaining)}원)보다 큽니다.`;
      }
    }
    // 수정에서는 광고문구를 비워도 저장 가능 (입력칸 값 그대로 저장)
    const adText = (giftDesc || '').trim();
    if (adText.length > 100) {
      return t.giftAdMaxLength;
    }
    if (!imageFileName && !giftData.thumbnailFileUrl && !giftData.giftImageNo) {
      return t.giftPhotoRequired;
    }
    const saleStart = toSeoulDateString(giftData.saleStartDttm);
    const todayStr = toSeoulDateString(new Date());
    if (!saleStart || saleStart < todayStr) {
      return t.giftSaleStartDateInvalid;
    }
    // 유효기간은 항상 3개월로 고정
    if (validityMonth !== '3') {
      setValidityMonth('3');
      setValidityMonthPre('3');
      setIsValidityChecked(true);
    }
    return true;
  };

  const modGift = async _unused => {
    try {
      const validationResult = checkModGift();
      if (validationResult === true) {
        const formData = new FormData();
        formData.append('giftNo', giftData.giftNo);
        formData.append('giftName', giftData.giftName);
        formData.append('categoryCode', giftData.categoryCode);
        // 비운 경우에도 빈 값으로 보내 새 버전에 기존 문구가 남지 않게 한다
        formData.append('giftDesc', (giftDesc || '').trim());

        const bpNum = parseInt(delComString(String(giftData.basePrice ?? 0))) || 0;
        const dpNum = discountPriceNumber; // 프로모션 없으면 0 — 할인 없이 저장
        formData.append('basePrice', bpNum);
        if (dpNum > 0) formData.append('discountPrice', bpNum - dpNum);
        formData.append(
          'saleStartDttm',
          toSeoulDateString(giftData.saleStartDttm)
        );
        if (saleEndDttm) {
          formData.append('saleEndDttm', saleEndDttm);
        }
        // 바우처 실증기간(~2026-09-30) 종료 — 바우처 미적용(N) + 유효기간 3개월 고정으로만 저장한다.
        formData.append('validity', '3M');
        if (!isCountIssueChecked) {
          formData.append('countIssued', Number(delComString(String(countIssue))));
        }
        formData.append('transferTime', '1M');
        // formData.append('voucherUseYn', 'N');
        // 프로모션 선택 여부로 voucherUseYn 결정 (바우처 토글 UI 없음)
        // [2026-10-21] 연결된 프로모션이 기간 종료 등으로 선택 목록에 없으면 연결을 해제해 저장한다
        //  (그대로 보내면 서버가 "이 상점에서 선택할 수 없는 프로모션입니다"로 거부)
        const promotionNoToSave = isStaleVoucher ? '' : giftData.discountPromotionNo;
        formData.append('voucherUseYn', promotionNoToSave ? 'Y' : 'N');
        formData.append('couponEligibleYn', giftData.couponEligibleYn || 'Y');
        // [2026-10-27] 고른 쿠폰이 없고 고를 쿠폰도 없거나 목록을 못 받았으면 안 보낸다 — 서버가 이전 버전 선택을 이어받음
        if (
          (giftData.couponEligibleYn || 'Y') === 'Y' &&
          couponsLoaded &&
          (couponCandidates.length > 0 || selectedCouponNos.length > 0)
        ) {
          formData.append('couponPromotionNos', selectedCouponNos.join(','));
        }
        if (promotionNoToSave) {
          formData.append('discountPromotionNo', promotionNoToSave);
        }
        if (stopDesc) {
          formData.append('state', 'SS003');
          formData.append('stopDesc', stopDesc);
        }

        if (imageFile) {
          if (imageFile !== giftData.thumbnailFileUrl) {
            formData.append('file', imageFileName);
          } else if (giftData.giftImageNo) {
            formData.append('giftImageNo', giftData.giftImageNo);
          }
        }

        // 기프트 정보 수정
        const ret = await fetchData({
          url: `${Config.API_SERVER}gift`,
          method: 'put',
          data: formData,
          // cookie-based auth
        });
        if (isSuccessResponse(ret)) {
          setModDone(true);
          setModalInfo({
            ...modalInfo,
            actionFlag: 'modSuccess',
            isCancel: false,
            mainMsg: t.modifySuccess,
          });
          setModalOpen(true);
        } else {
          setModalInfo({
            ...modalInfo,
            actionFlag: 'api',
            isCancel: false,
            mainMsg: getErrorMessage(ret),
          });
          setModalOpen(true);
        }
      } else {
        setModalInfo({
          ...modalInfo,
          actionFlag: 'api',
          isCancel: false,
          mainMsg: validationResult,
        });
        setModalOpen(true);
      }
    } catch (error) {
      const errResp = error?.response;
      setModalInfo({
        ...modalInfo,
        actionFlag: 'api',
        isCancel: false,
        mainMsg: getErrorMessage(errResp),
      });
      setModalOpen(true);
      console.error('Error fetching data:', error);
    }
  };

  const toggleSale = async () => {
    try {
      // SS001(판매예정): 판매시작일을 오늘로 설정 → SS002(판매시작)로 전환
      const url =
        giftData.state === 'SS001'
          ? `${Config.API_SERVER}gift/startSale/${giftNo}`
          : `${Config.API_SERVER}gift/toggle/${giftNo}`;
      const ret = await fetchData({ url, method: 'patch' });
      if (isSuccessResponse(ret)) {
        await getData();
        setModalInfo({
          ...modalInfo,
          actionFlag: 'modSuccess',
          isCancel: false,
          mainMsg: t.saleStateChanged,
        });
        setModalOpen(true);
      } else {
        setModalInfo({
          ...modalInfo,
          actionFlag: 'api',
          isCancel: false,
          mainMsg: getErrorMessage(ret),
        });
        setModalOpen(true);
      }
    } catch (error) {
      setModalInfo({
        ...modalInfo,
        actionFlag: 'api',
        isCancel: false,
        mainMsg: getErrorMessage(error?.response),
      });
      setModalOpen(true);
    }
  };
  /* 판매중지
  const stopGift = async () => {
    try {
      if (!stopDesc) {
        setModalInfo({ ...modalInfo, actionFlag: 'stop', isStopFirst: false });
        setModalOpen(true);
      } else {
        modalInfo.actionFlag = '';
      }
    } catch (error) {
      // 에러 발생 시의 코드

      if (error.response) {
        if (error.response.data.resultCode === 401) {
          setModalInfo({
            ...modalInfo,
            actionFlag: 'goMain',
            mainMsg: t.loginExpired,
          });
          setModalOpen(true);
        } else {
          setModalInfo({ ...modalInfo, actionFlag: 'api' });
          openModal();
        }
      } else if (error.request) {
        // 요청은 전송되었지만 응답이 없는 경우

        setModalInfo({ ...modalInfo, actionFlag: 'api' });
        openModal();
      } else {
        // 요청을 설정하는 중에 에러가 발생한 경우

        setModalInfo({ ...modalInfo, actionFlag: 'api' });
        openModal();
      }
      console.error('Error fetching data:', error);
    }
  };
*/
  const delGift = async _unused => {
    try {
      const ret = await fetchData({
        url: `${Config.API_SERVER}gift/${giftNo}`,
        method: 'delete',
      });
      if (isSuccessResponse(ret)) {
        navigate(`/${storeNo}/gift-list`);
      } else {
        setModalInfo({
          ...modalInfo,
          actionFlag: 'api',
          isCancel: false,
          mainMsg: getErrorMessage(ret),
        });
        setModalOpen(true);
      }
    } catch (error) {
      setModalInfo({
        ...modalInfo,
        actionFlag: 'api',
        isCancel: false,
        mainMsg: getErrorMessage(error?.response),
      });
      setModalOpen(true);
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modDone, giftNo]);

  useEffect(() => {
    if (!storeNo) return;
    // 상점 바우처 잔액 조회 — 잔액 표시 제거(upstream 66270b0)로 미사용
    // fetchData({ url: `${Config.API_SERVER}store/${storeNo}`, method: 'get' })
    //   .then(res => {
    //     if (res?.data?.data?.data) setStoreVoucherInfo(res.data.data.data);
    //   })
    //   .catch(() => {});
    fetchData({
      url: `${Config.API_SERVER}gift/available-vouchers/${storeNo}`,
      method: 'get',
    })
      .then(res => {
        const list = res?.data?.data;
        if (Array.isArray(list)) {
          setAvailableVouchers(list);
          setVouchersLoaded(true);
        }
      })
      .catch(() => {});
    // 이 상점에서 고를 수 있는 COUPON 목록 (쿠폰 적용 허용 체크박스용)
    fetchData({
      url: `${Config.API_SERVER}gift/available-coupons/${storeNo}`,
      method: 'get',
    })
      .then(res => {
        const list = res?.data?.data;
        if (Array.isArray(list)) {
          setCouponCandidates(list);
          setCouponsLoaded(true);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeNo]);

  // [2026-10-27] 선택 기록이 없는 기존 허용 기프트는 지금 쓸 수 있는 쿠폰을 전부 체크해 둔 상태로 연다
  //  (저장하면 그때부터 선택 방식 — 가격만 고치려는 상점이 0개 저장 금지에 막히지 않게)
  useEffect(() => {
    if (!pendingSelectAllCoupons || !couponsLoaded) return;
    setSelectedCouponNos(couponCandidates.map(c => Number(c.promotionNo)));
    setPendingSelectAllCoupons(false);
  }, [pendingSelectAllCoupons, couponsLoaded, couponCandidates]);

  return (
    <>
      <AlertModal
        open={modalOpen}
        close={closeModal}
        action={doModal}
        header={modalInfo.headerMsg}
        isConfirm={modalInfo.isConfirm}
        isMessage={modalInfo.isMessage}
        isCancel={modalInfo.isCancel}
      >
        {modalInfo.actionFlag === 'stop' ? (
          <>{modalInfo.mainMsg}</>
        ) : (
          <>
            {modalInfo.actionFlag === 'adCopyLoading' ? (
              <div className="ad-copy-generating">
                <div className="ad-copy-generating__visual" aria-hidden="true">
                  <div className="ad-copy-generating__glow" />
                  <div className="ad-copy-generating__ring ad-copy-generating__ring--a" />
                  <div className="ad-copy-generating__ring ad-copy-generating__ring--b" />
                  <div className="ad-copy-generating__core">
                    <span className="ad-copy-generating__spark" />
                  </div>
                </div>
                <div className="ad-copy-generating__title">
                  광고 문구 생성 중
                </div>
                <div className="ad-copy-generating__sub">
                  생성형 AI가 매장·상품 정보를 바탕으로 후보 5개를 만들고
                  있어요.
                </div>
                <div className="ad-copy-generating__dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            ) : modalInfo.actionFlag === 'openai' ? (
              <div className="ad-copy-modal">
                <div className="ad-copy-modal__title">{t.giftAdSelect}</div>
                <div className="ad-copy-modal__desc">
                  생성된 문구 중 하나를 선택하면 입력창에 바로 반영됩니다.
                </div>
                <div className="ad-copy-modal__list">
                  {openAIGiftDescData.map((item, index) => {
                    const id = `ad-copy-manage-${index}`;
                    const checked = (openAISelectedData || '') === item;
                    return (
                      <label
                        key={id}
                        className={`ad-copy-option${checked ? ' is-selected' : ''}`}
                        htmlFor={id}
                      >
                        <input
                          id={id}
                          type="radio"
                          name="ad-copy-manage"
                          value={item}
                          checked={checked}
                          onChange={giftDescChange}
                        />
                        <span className="ad-copy-option__index">
                          {index + 1}
                        </span>
                        <span className="ad-copy-option__text">{item}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>{modalInfo.mainMsg}</>
            )}
          </>
        )}
      </AlertModal>

      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>
              {t.home}
            </NavLink>
          </li>
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>
              {t.giftManagement}
            </NavLink>
          </li>
          <li>{t.giftModifyDelete}</li>
        </ul>
      </section>

      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="gift" />

          <section className="rightcontant_box">
            <h2 className="tit_01">{t.giftModifyDelete}</h2>

            <article className="form_card contants_box_03">
              <table className="member_box">
                <tbody>
                  <tr>
                    <th>{t.giftSerial}</th>
                    <td>
                      <form>
                        <input
                          className="input_03"
                          type="text"
                          style={{
                            backgroundColor: '#ddd',
                            fontFamily: 'inherit',
                          }}
                          name=""
                          disabled
                          value={formatGiftSerialDisplay(giftData)}
                        />
                      </form>
                    </td>
                  </tr>
                  <tr>
                    <th>{t.giftName}</th>
                    <td>
                      <form>
                        {giftData.state === 'SS004' ||
                        giftData.state === 'SS005' ? (
                          <input
                            className="input_03"
                            type="text"
                            name=""
                            disabled
                            style={{ backgroundColor: '#ddd' }}
                            value={giftData.giftName || ''}
                          />
                        ) : (
                          <input
                            className="input_03"
                            type="text"
                            name=""
                            maxLength={50}
                            value={giftData.giftName || ''} // 빈 문자열로 기본값
                            onChange={e =>
                              setGiftData({
                                ...giftData,
                                giftName: e.target.value,
                              })
                            }
                          />
                        )}
                        {giftData.giftName &&
                          giftData.giftName.length >= 50 && (
                            <div className="font_red">{t.giftNameMaxChars}</div>
                          )}
                      </form>
                    </td>
                  </tr>
                  <tr>
                    <th>{t.category}</th>
                    <td>
                      <form>
                        {giftData.state === 'SS004' ||
                        giftData.state === 'SS005' ? (
                          <input
                            className="input_01"
                            type="text"
                            name=""
                            disabled
                            style={{ backgroundColor: '#ddd' }}
                            value={getCategoryLabel(giftData.categoryCode)}
                          />
                        ) : (
                          <select
                            className="input_01 select_01 gift-category-select"
                            onChange={categoryChange}
                            value={giftData.categoryCode || ''}
                          >
                            {categoryData.map((category, index) => (
                              <option key={index} value={category.detailCode}>
                                {t.selectCategory[category.detailCodeName] ||
                                  category.detailCodeName}
                              </option>
                            ))}
                          </select>
                        )}
                      </form>
                    </td>
                  </tr>
                  <tr>
                    <th>{t.giftPhoto}</th>
                    <td>
                      <div className="gift-photo-upload">
                        <div className="gift-photo-upload__preview">
                          {(imageFile || giftData.thumbnailFileUrl) && (
                            <img
                              src={imageFile || giftData.thumbnailFileUrl}
                              alt="Selected"
                            />
                          )}
                        </div>
                        <div className="gift-photo-upload__actions">
                          {giftData.state !== 'SS004' &&
                            giftData.state !== 'SS005' && (
                              <>
                                <label className="gift-photo-upload__btn">
                                  <input
                                    type="file"
                                    name="fileInput"
                                    onChange={changeFile}
                                    accept=".jpg, .jpeg, .gif, .png"
                                  />
                                  {t.selectImage}
                                </label>
                                <p className="gift-photo-upload__note font_gray">
                                  {t.giftUploadFileNote}
                                </p>
                                {imageError && (
                                  <div className="font_red">{imageError}</div>
                                )}
                              </>
                            )}
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>{t.giftAd}</th>
                    <td>
                      <form>
                        {giftData.state === 'SS004' ||
                        giftData.state === 'SS005' ? (
                          <input
                            className="input_04"
                            type="text"
                            name=""
                            disabled
                            style={{ backgroundColor: '#ddd' }}
                            value={giftData.giftDesc || ''}
                          />
                        ) : (
                          <>
                            <input
                              className="input_04"
                              type="text"
                              name=""
                              maxLength={100}
                              value={giftDesc || ''}
                              onChange={e => setGiftDesc(e.target.value)}
                            />
                            {giftDesc && giftDesc.length >= 100 && (
                              <div className="font_red">{t.giftAdMaxChars}</div>
                            )}
                            {openAIGiftDescData.length > 0 && (
                              <div style={{ marginTop: '8px' }}>
                                <select
                                  className="input_01 select_01 gift-category-select"
                                  onChange={giftDescChange}
                                  value={openAISelectedData || ''}
                                >
                                  {openAIGiftDescData.map((item, index) => (
                                    <option key={index} value={item}>
                                      {item}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </>
                        )}
                      </form>
                    </td>
                  </tr>
                  <tr>
                    {/* [2026-10-19] 바우처 적용 토글·상점 바우처 잔액 — upstream 66270b0 반영, 복구 안 함
                    <th>바우처 적용</th>
                    <td>
                      {giftData.state === 'SS004' || giftData.state === 'SS005' ? (
                        <input
                          className="input_01"
                          type="text"
                          disabled
                          style={{ backgroundColor: '#ddd' }}
                          value={giftData.voucherUseYn === 'Y' ? '적용 (Y)' : '미적용 (N)'}
                        />
                      ) : (
                        <select
                          className="input_01 select_01"
                          value={giftData.voucherUseYn || 'N'}
                          onChange={e => setGiftData({ ...giftData, voucherUseYn: e.target.value })}
                        >
                          <option value="N">미적용 (N)</option>
                          <option value="Y">적용 (Y)</option>
                        </select>
                      )}
                      {storeVoucherInfo && (
                        <span style={{ marginLeft: '10px', fontSize: '12px', color: '#555' }}>
                          💳 상점 바우처 잔액:&nbsp;
                          <strong style={{ color: '#1a56db' }}>
                            {formatNumNumber(storeVoucherInfo.voucherBalance ?? 0)}원
                          </strong>
                        </span>
                      )}
                    </td>
                  </tr>
                  {(giftData.voucherUseYn || 'N') === 'Y' && (
                  <tr>
                    */}
                    <th>바우처 프로모션 선택</th>
                    <td>
                      <select
                        className="input_01 select_01"
                        value={giftData.discountPromotionNo || ''}
                        disabled={giftData.state === 'SS004' || giftData.state === 'SS005'}
                        onChange={e => {
                          // [2026-10-30] 프로모션을 빼면 할인가격도 비운다(할인은 바우처 프로모션 적용 시에만)
                          setGiftData({
                            ...giftData,
                            discountPromotionNo: e.target.value,
                            ...(!e.target.value ? { discountPrice: '' } : {}),
                          });
                          if (!e.target.value) setDiscountPriceError('');
                        }}
                      >
                        <option value="">
                          {availableVouchers.length
                            ? '선택 안 함'
                            : '선택 가능한 바우처 프로모션 없음'}
                        </option>
                        {availableVouchers.map(v => (
                          <option key={v.promotionNo} value={v.promotionNo}>
                            [{ISSUER_LABEL[v.issuerType] || v.issuerType}] {v.name} (잔여{' '}
                            {formatNumNumber((v.totalAmount ?? 0) - (v.usedAmount ?? 0))}원)
                          </option>
                        ))}
                      </select>
                      <span style={{ marginLeft: '10px', fontSize: '12px', color: '#888' }}>
                        선택하면 할인가격만큼 이 프로모션 예산에서 차감됩니다
                      </span>
                      {isStaleVoucher && (
                        <div style={{ marginTop: '6px', fontSize: '12px', color: '#d93025' }}>
                          연결돼 있던 프로모션(#{giftData.discountPromotionNo})은 기간이 끝났거나 사용할 수 없어,
                          저장하면 프로모션 연결이 해제됩니다.
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>쿠폰 적용 허용</th>
                    <td>
                      <select
                        className="input_01 select_01"
                        value={giftData.couponEligibleYn || 'Y'}
                        onChange={e => {
                          setGiftData({ ...giftData, couponEligibleYn: e.target.value });
                          // 불가로 바꾸면 선택값을 비운다
                          if (e.target.value !== 'Y') setSelectedCouponNos([]);
                          setCouponSelectError('');
                        }}
                      >
                        <option value="Y">허용 (Y)</option>
                        <option value="N">불가 (N)</option>
                      </select>
                      <span style={{ marginLeft: '10px', fontSize: '12px', color: '#888' }}>
                        구매자가 결제 시 프로모션 쿠폰을 쓸 수 있는지
                      </span>
                      {(giftData.couponEligibleYn || 'Y') === 'Y' && (
                        <GiftCouponPicker
                          candidates={couponCandidates}
                          selected={selectedCouponNos}
                          endedSelected={endedCoupons.filter(
                            c => !couponCandidates.some(x => Number(x.promotionNo) === Number(c.promotionNo)),
                          )}
                          loaded={couponsLoaded}
                          disabled={giftData.state === 'SS004' || giftData.state === 'SS005'}
                          error={couponSelectError}
                          onChange={next => {
                            setSelectedCouponNos(next);
                            if (next.length) setCouponSelectError('');
                          }}
                        />
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>정상가격</th>
                    <td>
                      <form>
                        <input
                          className="input_01"
                          type="text"
                          maxLength={11}
                          disabled={giftData.state === 'SS004' || giftData.state === 'SS005'}
                          style={{
                            textAlign: 'right',
                            ...(giftData.state === 'SS004' || giftData.state === 'SS005'
                              ? { backgroundColor: '#ddd' }
                              : {}),
                          }}
                          value={formatNumNumber(giftData.basePrice) || ''}
                          onChange={e => {
                            const noComma = e.target.value.replace(/,/g, '');
                            if (/^\d*$/.test(noComma)) {
                              const numeric = Number(noComma || '0');
                              setBasePriceError(
                                numeric > 1000000
                                  ? t.giftPriceMaxManage
                                  : numeric !== 0 && numeric % 100 !== 0
                                    ? t.giftPriceUnit
                                    : ''
                              );
                            } else {
                              setBasePriceError(t.numbersOnly);
                            }
                            setGiftData({ ...giftData, basePrice: formatNumNumber(noComma) });
                          }}
                        />
                        &ensp;{t.currency}
                      </form>
                      {basePriceError && <div className="font_red">{basePriceError}</div>}
                    </td>
                  </tr>
                  <tr>
                    <th>할인가격</th>
                    <td>
                      <form>
                        <input
                          className="input_01"
                          type="text"
                          maxLength={11}
                          placeholder={canDiscount || isPriceLocked ? '없으면 비워두세요' : '바우처 프로모션 선택 시 입력'}
                          disabled={isPriceLocked || !canDiscount}
                          style={{
                            textAlign: 'right',
                            ...(isPriceLocked || !canDiscount ? { backgroundColor: '#ddd' } : {}),
                          }}
                          value={discountPriceNumber > 0 ? formatNumNumber(discountPriceNumber) : ''}
                          onChange={e => {
                            const noComma = e.target.value.replace(/,/g, '');
                            if (/^\d*$/.test(noComma)) {
                              const numeric = Number(noComma || '0');
                              setDiscountPriceError(numeric % 100 !== 0 ? t.giftPriceUnit : '');
                            } else {
                              setDiscountPriceError(t.numbersOnly);
                            }
                            setGiftData({ ...giftData, discountPrice: noComma ? formatNumNumber(noComma) : '' });
                          }}
                        />
                        &ensp;{t.currency}
                      </form>
                      {discountPriceError && <div className="font_red">{discountPriceError}</div>}
                      {isDiscountDropped && (
                        <div style={{ marginTop: '6px', fontSize: '12px', color: '#d93025' }}>
                          바우처 프로모션이 연결돼 있지 않아, 저장하면 기존 할인({formatNumNumber(loadedDiscountNumber)}원)이
                          해제되고 판매가가 정상가격으로 바뀝니다.
                        </div>
                      )}
                      {discountPriceNumber > 0 && basePriceNumber > 0 && (
                        <div style={{ marginTop: '6px', fontSize: '12px', color: '#888' }}>
                          정상가격 {formatNumNumber(basePriceNumber)}원 − {formatNumNumber(discountPriceNumber)}원 = <strong style={{ color: '#e53e3e' }}>{formatNumNumber(effectivePrice)}원</strong>
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>
                      {t.price}
                      <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#aaa', marginTop: '2px' }}>최종 결제금액</div>
                    </th>
                    <td>
                      <input
                        className="input_01"
                        type="text"
                        disabled
                        style={{ backgroundColor: '#f0f4ff', textAlign: 'right', fontWeight: 'bold', color: '#1a56db', border: '1px solid #c3d3f7' }}
                        value={effectivePrice > 0 ? formatNumNumber(effectivePrice) : ''}
                      />
                      &ensp;{t.currency}
                      {isStampDutyTarget && (
                        <div className="gift-price-notice">
                          {t.giftStampDutyNotice}
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>{t.salePeriod}</th>
                    <td>
                      <form>
                        {giftData.state === 'SS004' ||
                        giftData.state === 'SS005' ? (
                          <>
                            <input
                              className="input_05"
                              type="text"
                              name=""
                              disabled
                              style={{ backgroundColor: '#ddd' }}
                              value={saleStartDttmPre || ''}
                            />
                            &ensp;&ensp;&ensp;~&ensp;&ensp;&ensp;
                            <input
                              className="input_05"
                              type="text"
                              name=""
                              disabled
                              style={{ backgroundColor: '#ddd' }}
                              value={saleEndDttmPre || ''}
                            />
                          </>
                        ) : (
                          <>
                            <LocaleDateInput
                              className="input_05"
                              minDate={getFormattedDateTime(new Date()).slice(
                                0,
                                10
                              )}
                              value={
                                giftData.saleStartDttm
                                  ? toSeoulDateString(giftData.saleStartDttm)
                                  : ''
                              }
                              onChange={val => {
                                setGiftData({
                                  ...giftData,
                                  saleStartDttm: val,
                                  saleEndDttm:
                                    giftData.saleEndDttm &&
                                    toSeoulDateString(giftData.saleEndDttm) <
                                      val
                                      ? val
                                      : giftData.saleEndDttm,
                                });
                              }}
                            />
                            &ensp;&ensp;&ensp;~&ensp;&ensp;&ensp;
                            <LocaleDateInput
                              className="input_05"
                              minDate={
                                giftData.saleStartDttm
                                  ? toSeoulDateString(giftData.saleStartDttm)
                                  : undefined
                              }
                              disabled={isSalePeriodChecked}
                              style={{
                                backgroundColor: isSalePeriodChecked
                                  ? '#ddd'
                                  : undefined,
                              }}
                              value={saleEndDttm ? saleEndDttm : ''}
                              onChange={val => {
                                setSaleEndDttm(val);
                                setSaleEndDttmPre(val);
                              }}
                            />
                            <label
                              className="check_box"
                              style={{ marginLeft: '20px' }}
                            >
                              &ensp;{t.giftUnlimited}
                              <input
                                type="checkbox"
                                checked={isSalePeriodChecked}
                                onChange={salePeriodCheckboxChange}
                              />
                              <span className="check_check"></span>
                            </label>
                          </>
                        )}
                      </form>
                      {!(
                        giftData.state === 'SS004' || giftData.state === 'SS005'
                      ) && <div className="font_red">{t.datePickerOnly}</div>}
                    </td>
                  </tr>
                  <tr>
                    <th>{t.validityPeriod}</th>
                    <td>
                      {/* 저장은 항상 3개월(validity '3M').
                          [2026-10-28] 전엔 "바우처 연결 = 2026-09-30 고정"으로 그려서, 프로모션 바우처를 붙인 새 기프트에도
                          실증기간 만료일이 잘못 보였다 → 바우처 여부가 아니라 실제 저장된 유효기간 값으로 판단한다.
                          만료일을 날짜로 지정한 옛 기프트(실증기간)는 판매종료 · 취소(수정 불가) 상태일 때만 그 날짜를 보여준다 —
                          수정 가능한 기프트는 저장하면 3개월로 바뀌므로 3개월로 보여준다. */}
                      {(giftData.state === 'SS004' || giftData.state === 'SS005') &&
                      /^\d{4}-\d{2}-\d{2}$/.test(giftData.validity || '') ? (
                        <>
                          <input
                            className="input_02"
                            type="text"
                            name=""
                            disabled
                            readOnly
                            style={{
                              backgroundColor: '#ddd',
                              textAlign: 'right',
                              width: '140px',
                            }}
                            value={giftData.validity}
                          />
                          <span
                            style={{
                              marginLeft: '10px',
                              color: '#666',
                              fontSize: '12px',
                            }}
                          >
                            (만료일 지정 기프트)
                          </span>
                        </>
                      ) : (
                        <>
                          {t.purchaseAfter}&ensp;&ensp;&ensp;
                          <input
                            className="input_02"
                            type="text"
                            name=""
                            disabled
                            readOnly
                            style={{
                              backgroundColor: '#ddd',
                              textAlign: 'right',
                            }}
                            value="3"
                          />
                          &ensp;{t.month}
                          <span
                            style={{
                              marginLeft: '10px',
                              color: '#666',
                              fontSize: '12px',
                            }}
                          >
                            {t.validityFixed}
                          </span>
                        </>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>{t.totalIssuedQuantity}</th>
                    <td>
                      <form>
                        {giftData.state === 'SS004' ||
                        giftData.state === 'SS005' ? (
                          <>
                            <input
                              className="input_05"
                              type="text"
                              name=""
                              disabled
                              style={{
                                backgroundColor: '#ddd',
                                textAlign: 'right',
                              }}
                              value={countIssue !== '0' ? countIssue : ''}
                            />
                            &ensp;{t.count}
                          </>
                        ) : (
                          <>
                            <input
                              className="input_05"
                              type="text"
                              name=""
                              style={{
                                textAlign: 'right',
                                backgroundColor: isCountIssueChecked
                                  ? '#ddd'
                                  : undefined,
                              }}
                              maxLength={11}
                              disabled={isCountIssueChecked}
                              value={countIssue !== '0' ? countIssue : ''}
                              onChange={e => {
                                const raw = e.target.value.replace(/,/g, '');
                                if (/^\d*$/.test(raw)) {
                                  setCountIssueError('');
                                } else {
                                  setCountIssueError(t.numbersOnly);
                                }
                                setCountIssue(formatStrNumber(raw));
                                setCountIssuePre(formatNumNumber(raw));
                              }}
                            />
                            &ensp;{t.count}&ensp;&ensp;&ensp;
                            <label className="check_box">
                              &ensp;{t.unlimited}
                              <input
                                type="checkbox"
                                checked={isCountIssueChecked}
                                onChange={countIssueCheckboxChange}
                              />
                              <span className="check_check"></span>
                            </label>
                          </>
                        )}
                      </form>
                      {countIssueError && (
                        <div className="font_red">{countIssueError}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>{t.soldQuantity}</th>
                    <td>
                      <form>
                        <input
                          className="input_01"
                          type="text"
                          name=""
                          style={{
                            backgroundColor: '#ddd',
                            textAlign: 'right',
                          }}
                          value={formatNumNumber(giftData.countSold) || 0}
                          disabled
                        />
                        &ensp;{t.count}
                      </form>
                    </td>
                  </tr>
                  <tr>
                    <th>{t.soldAmount}</th>
                    <td>
                      <form>
                        <input
                          className="input_01"
                          type="text"
                          name=""
                          style={{
                            backgroundColor: '#ddd',
                            textAlign: 'right',
                          }}
                          value={
                            giftData.countSold
                              ? formatNumNumber(
                                  giftData.countSold * giftData.price
                                )
                              : 0
                          }
                          disabled
                        />
                        &ensp;{t.currency}
                      </form>
                    </td>
                  </tr>
                  <tr>
                    <th>{t.state}</th>
                    <td>
                      <div className="m_salesstatus_box">
                        <ul className="m_salesstatus_01">
                          <li
                            className={
                              giftData.state === 'SS001'
                                ? 'font_red'
                                : undefined
                            }
                          >
                            {t.stateDesc['판매등록']} ({regDttm.slice(0, 10)})
                          </li>
                          <li
                            className={
                              giftData.state === 'SS002'
                                ? 'font_red'
                                : undefined
                            }
                          >
                            {t.stateDesc['판매시작']} ({saleStartDttmPre})
                          </li>
                          <li
                            className={
                              giftData.state === 'SS003' ||
                              giftData.state === 'SS004' ||
                              giftData.state === 'SS005'
                                ? 'font_red'
                                : 'font_gray'
                            }
                          >
                            {t.stateDesc['판매종료']} ({updDttm.slice(0, 10)})
                          </li>
                        </ul>
                        &nbsp;&nbsp;&nbsp;
                        {(giftData.state === 'SS001' ||
                          giftData.state === 'SS002' ||
                          giftData.state === 'SS003') && (
                          <button
                            type="button"
                            className={
                              giftData.state === 'SS001' ||
                              giftData.state === 'SS002'
                                ? 'button_05 button_yellow'
                                : 'button_05 button_black'
                            }
                            onClick={() => {
                              setModalInfo({
                                ...modalInfo,
                                actionFlag: 'stop',
                                isCancel: true,
                                mainMsg:
                                  giftData.state === 'SS001'
                                    ? t.confirmSaleStartReplace
                                    : giftData.state === 'SS003'
                                      ? t.confirmResume
                                      : t.confirmPause,
                              });
                              setModalOpen(true);
                            }}
                          >
                            {giftData.state === 'SS001'
                              ? t.saleStartBtn
                              : giftData.state === 'SS002'
                                ? t.salesPauseBtn
                                : t.salesResume}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {giftProductUrl && (
                    <tr>
                      <th>{t.giftProductQrLabel}</th>
                      <td>
                        {isZeroPriceGift ? (
                          <p style={{ margin: '0 0 12px', color: '#475569' }}>
                            {t.giftProductQrDescZero}
                          </p>
                        ) : null}
                        <div className="signup_06">
                          <div className="signup_06_002 signup_06_002_qrbox">
                            <div className={qrStyles.storeQrCard}>
                              <div className={qrStyles.storeQrHead}>
                                <div className={qrStyles.storeQrTitle}>
                                  {language === 'en'
                                    ? giftData.giftName || 'Gift'
                                    : giftData.giftName || '기프트'}{' '}
                                  {language === 'en'
                                    ? 'Product page'
                                    : '기프트 상세'}
                                  <br />
                                  <span className={qrStyles.storeQrScanText}>
                                    {language === 'en'
                                      ? 'Scan with your smartphone camera'
                                      : '스마트폰 카메라로 스캔하세요'}
                                  </span>
                                </div>
                              </div>
                              <div className={qrStyles.storeQrBody}>
                                {qrValue ? (
                                  <QRCode value={qrValue} size={120} />
                                ) : (
                                  <p style={{ margin: 0, color: '#b91c1c' }}>
                                    {language === 'en'
                                      ? 'QR is unavailable. Please contact support.'
                                      : 'QR 을 만들 수 없습니다. 고객센터로 문의해 주세요.'}
                                  </p>
                                )}
                              </div>
                              <div className={qrStyles.storeQrFooter}>
                                {language === 'en' ? (
                                  <>
                                    {
                                      'Visit Naegift (www.naegift.com) to discover a variety of gifts~'
                                    }
                                    <br />
                                    Powered by Authrium Co., Ltd.
                                  </>
                                ) : (
                                  <>
                                    {
                                      '내기프트(www.naegift.com)로 들어오시면 다양한 기프트를 만날 수 있어요~'
                                    }
                                    <br />
                                    Powered by (주)오스리움
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="button-container">
                            <div
                              ref={qrDownloadRef}
                              style={{
                                position: 'absolute',
                                left: -9999,
                                top: 0,
                                overflow: 'hidden',
                              }}
                            >
                              {qrValue && (
                                <QRCodeCanvas value={qrValue} size={120} />
                              )}
                            </div>
                            <a
                              href={giftProductUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <button
                                type="button"
                                className="button_05 button_gray"
                                style={{ width: '300px' }}
                              >
                                {t.movetoGiftProductPage}
                              </button>
                            </a>
                            {qrValue && (
                              <>
                                <button
                                  type="button"
                                  className="button_05 button_black"
                                  onClick={handleDownloadGiftProductQr}
                                >
                                  {t.downloadQR}
                                </button>
                                <button
                                  type="button"
                                  className="button_05 button_gray"
                                  onClick={handlePrintGiftProductQr}
                                >
                                  {t.printQR}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <th>{t.registrationDateTime}</th>
                    <td>
                      <form>
                        <input
                          className="input_01"
                          type="text"
                          name=""
                          disabled
                          style={{
                            backgroundColor: '#ddd',
                            textAlign: 'center',
                          }}
                          value={regDttm}
                        />
                      </form>
                    </td>
                  </tr>
                </tbody>
              </table>
            </article>

            <article className="button_box_01">
              {(giftData.state === 'SS001' ||
                giftData.state === 'SS002' ||
                giftData.state === 'SS003') && (
                <button
                  type="button"
                  className="button_01 button_03 button_gray_line font_red"
                  value={''}
                  onClick={e => {
                    modalInfo.actionFlag = 'del';
                    openModal(e);
                  }}
                >
                  {t.giftCancelSale}
                </button>
              )}
              <NavLink to={`/${storeNo}/gift-list`}>
                <button
                  type="button"
                  className="button_01 button_03 button_gray_line"
                >
                  {t.giftToList}
                </button>
              </NavLink>
              {(giftData.state === 'SS001' ||
                giftData.state === 'SS002' ||
                giftData.state === 'SS003') && (
                <button
                  type="button"
                  className="button_01 button_03 button_red"
                  value={''}
                  onClick={e => {
                    modalInfo.actionFlag = 'mod';
                    openModal(e);
                  }}
                >
                  {t.giftSave}
                </button>
              )}
            </article>
          </section>
        </section>
      </main>
    </>
  );
}
